import React from 'react';

interface LogoProps {
  className?: string;
  showText?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  variant?: 'light' | 'dark';
  showDecorations?: boolean;
}

export const Logo: React.FC<LogoProps> = ({ 
  className = '', 
  showText = true, 
  size = 'md', 
  variant = 'dark',
  showDecorations = false
}) => {
  const isDark = variant === 'dark';
  const textColor = isDark ? '#000000' : '#ffffff';
  
  // Set height/width classes based on size prop
  let sizeClasses = 'h-12 w-auto';
  if (size === 'sm') sizeClasses = 'h-8 w-auto';
  else if (size === 'lg') sizeClasses = 'h-24 w-auto';
  else if (size === 'xl') sizeClasses = 'h-36 w-auto';

  // Responsive viewBox and size based on options
  const viewBox = showText 
    ? (showDecorations ? "100 50 630 490" : "100 50 630 340")
    : "140 50 560 140";

  return (
    <svg 
      viewBox={viewBox} 
      className={`${sizeClasses} ${className}`}
      id="dakar-official-svg-logo"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* ================================================================= */}
      {/* 1. SENEGAL ROOFLINE STRIPES (TAPERED CRESCENT SHAPES)             */}
      {/* ================================================================= */}
      {/* Green Stripe (Top) */}
      <path 
        d="M 290,146 Q 440,55 590,146 Q 440,65 290,146 Z" 
        fill="#00853f" 
      />
      {/* Yellow Stripe (Middle) */}
      <path 
        d="M 300,149 Q 440,65 600,149 Q 440,75 300,149 Z" 
        fill="#fdef42" 
      />
      {/* Red Stripe (Bottom) */}
      <path 
        d="M 310,152 Q 440,75 610,152 Q 440,85 310,152 Z" 
        fill="#e31b23" 
      />

      {/* ================================================================= */}
      {/* 2. BLACK CAR SILHOUETTE BRUSH STROKE                              */}
      {/* ================================================================= */}
      <path 
        d="M 150,175 C 280,135 340,145 400,148 C 480,152 560,140 690,175 C 560,148 480,158 400,154 C 340,151 280,141 150,175 Z" 
        fill={textColor} 
      />

      {/* ================================================================= */}
      {/* 3. LOGO TYPOGRAPHY AND SUBTEXT                                    */}
      {/* ================================================================= */}
      {showText && (
        <>
          {/* "DAKAR" text - bold, wide modern styling */}
          <text 
            x="420" 
            y="245" 
            fill={textColor} 
            fontFamily="system-ui, -apple-system, 'SF Pro Display', 'Space Grotesk', sans-serif" 
            fontWeight="900" 
            fontSize="82px" 
            textAnchor="middle" 
            letterSpacing="10"
          >
            DAKAR
          </text>

          {/* "AUTO SALES" text - bold, red, centered */}
          <text 
            x="420" 
            y="312" 
            fill="#e31b23" 
            fontFamily="system-ui, -apple-system, 'SF Pro Display', 'Space Grotesk', sans-serif" 
            fontWeight="800" 
            fontSize="54px" 
            textAnchor="middle" 
            letterSpacing="12"
          >
            AUTO SALES
          </text>

          {/* Tagline Side Lines */}
          {/* Green Line (Left) */}
          <line 
            x1="160" 
            y1="363" 
            x2="245" 
            y2="363" 
            stroke="#00853f" 
            strokeWidth="3.5" 
          />
          {/* Red Line (Right) */}
          <line 
            x1="595" 
            y1="363" 
            x2="680" 
            y2="363" 
            stroke="#e31b23" 
            strokeWidth="3.5" 
          />

          {/* Tagline Text: "QUALITY VEHICLES, TRUSTED SERVICE." */}
          <text 
            x="420" 
            y="368" 
            fill={textColor} 
            fontFamily="system-ui, -apple-system, 'SF Pro Text', sans-serif" 
            fontWeight="700" 
            fontSize="15px" 
            textAnchor="middle" 
            letterSpacing="2.5"
          >
            QUALITY VEHICLES, TRUSTED SERVICE.
          </text>
        </>
      )}

      {/* ================================================================= */}
      {/* 4. DYNAMIC DECORATIVE BOTTOM STRIPE (OPTIONAL HERO DECORATION)     */}
      {/* ================================================================= */}
      {showText && showDecorations && (
        <g id="senegal-bottom-wave-accent">
          {/* Green stripe */}
          <path 
            d="M 100,430 Q 350,490 650,520 Q 350,500 100,442 Z" 
            fill="#00853f" 
          />
          {/* Yellow stripe */}
          <path 
            d="M 100,442 Q 350,500 650,520 Q 350,510 100,454 Z" 
            fill="#fdef42" 
          />
          {/* Red stripe */}
          <path 
            d="M 100,454 Q 350,510 650,520 Q 350,520 100,466 Z" 
            fill="#e31b23" 
          />
          {/* Black bottom backing */}
          <path 
            d="M 100,466 Q 350,520 650,520 Q 350,530 100,485 Z" 
            fill="#000000" 
          />
        </g>
      )}
    </svg>
  );
};
